import T from "prop-types";
import React from "react";
import _, { get } from "lodash";
import { Field, formValueSelector } from "redux-form";
import { connect } from "react-redux";
import { Col, FormGroup } from "react-bootstrap";
import { NoteTooltip } from "MODULES/shared/components/NoteTooltip";
import { shortDateYYandTime } from "LIB/formatters/date";
import * as actions from "MODULES/companies/actions";
import {
  form,
  Form as AppForm,
  FormInput,
  FormRow,
  Select,
  Check,
  TextArea
} from "MODULES/shared/components/Forms";
import {
  QUALITY_CONTROL,
  ACCOUNTING,
  QUALITY_CONTROL_ADMIN,
  QUALITY_CONTROL_MANAGER,
  CAN_ADD_COMPANIES,
  CAN_UPDATE_COMPANIES
} from "MODULES/users/constants";
import { disabledUnless } from "MODULES/shared/decorators";
import {
  ASA_APPROVED,
  CAAC_APPROVED,
  COMPANY_STATUSES,
  DO_NOT_EXCHANGE,
  EASA_APPROVED,
  EXPORT_COMPLIANT,
  FAA_APPROVED,
  MANDATORY_ADDITIONAL_VERBIAGE,
  PASS_FAIL,
  STATUS,
  TCCA_APPROVED,
  OTHER_APPROVED,
  VENDOR_TRACE_PREAPPROVAL_REQUIRED,
  UK_CAA_APPROVED,
  EXPORT_APPROVAL_REQUIRED,
  END_USER_AGREEMENT_REQUESTED
} from "MODULES/companies/constants";
import {
  hasAnyOfSubroles,
  hasPermission,
  hasRole,
  hasSubRole
} from "MODULES/users/helpers";
import { properCase } from "LIB/formatters/text";
import { toOptions } from "LIB/forms";
import MonthYearField from "APP_ROOT/modules/shared/components/Forms/MonthYearField/MonthYearField";
import { withFieldAccessors } from "MODULES/shared/decorators/withFieldAccessors";
import validate from "./validate";
import RepairStationApproval from "./RepairStationApproval";
import VendorApproval from "./VendorApproval";

const StatusSelect = disabledUnless(
  user =>
    hasRole(user.toJS(), ACCOUNTING) ||
    hasPermission(user.toJS(), [CAN_ADD_COMPANIES, CAN_UPDATE_COMPANIES]) ||
    hasSubRole(user.toJS(), QUALITY_CONTROL_MANAGER)
)(props => (
  <FormRow>
    <Col md={12}>
      <Field
        name={STATUS}
        component={Select}
        label={properCase(STATUS)}
        options={toOptions(COMPANY_STATUSES)}
        disabled={props.disabled}
      />
    </Col>
  </FormRow>
));

const ASASelect = disabledUnless(
  user =>
    hasRole(user.toJS(), QUALITY_CONTROL) ||
    hasPermission(user.toJS(), [CAN_ADD_COMPANIES, CAN_UPDATE_COMPANIES])
)(props => (
  <Field
    name={ASA_APPROVED}
    component={Check}
    label="AC-0056B"
    disabled={props.disabled}
  />
));

const BlanketExchangeAgreement = disabledUnless(user =>
  hasPermission(user.toJS(), [CAN_ADD_COMPANIES, CAN_UPDATE_COMPANIES])
)(props => (
  <Col md={6}>
    <label
      className="control-label"
      style={{ display: "flex" }}
      htmlFor="blanket_exchange_agreement"
    >
      Blanket Exchange Agreement
    </label>
    <FormGroup>
      <div
        className="btn-group btn-group-justified btn-group-vertical"
        data-toggle="buttons"
      >
        <Field
          name="blanket_exchange_agreement"
          component={Check}
          label="Blanket Exchange Agreement"
          disabled={props.disabled}
        />
      </div>
    </FormGroup>
  </Col>
));

const doNotQuoteNote =
  "To be used only for companies that rarely buy from us or are pricing us.";
const DoNotQuote = disabledUnless(user =>
  hasPermission(user.toJS(), [CAN_ADD_COMPANIES, CAN_UPDATE_COMPANIES])
)(props => (
  <Col md={6}>
    <label className="control-label" htmlFor="do_not_quote">
      <span>Do Not Quote</span>
      <span style={{ marginLeft: 5 }}>
        <NoteTooltip
          note={doNotQuoteNote}
          placement="left"
          icon="fa-info-circle"
          color="rgba(255, 255, 255, 0.25)"
        />
      </span>
    </label>
    <FormGroup>
      <div
        className="btn-group btn-group-justified btn-group-vertical"
        data-toggle="buttons"
      >
        <Field
          name="do_not_quote"
          component={Check}
          label="Do Not Quote"
          disabled={props.disabled}
        />
      </div>
    </FormGroup>
  </Col>
));

const ExportCompliant = disabledUnless(
  user =>
    hasRole(user.toJS(), QUALITY_CONTROL) ||
    hasPermission(user.toJS(), [CAN_ADD_COMPANIES, CAN_UPDATE_COMPANIES])
)(props => (
  <Field
    name={EXPORT_COMPLIANT}
    component={Check}
    label={properCase(EXPORT_COMPLIANT)}
    disabled={props.disabled}
    onChange={val => {
      if (val) {
        props.setFieldValue(EXPORT_APPROVAL_REQUIRED)(false);
        props.setFieldValue(END_USER_AGREEMENT_REQUESTED)(false);
      }
    }}
  />
));

const TracePreapproval = disabledUnless(
  user =>
    hasAnyOfSubroles(user.toJS(), [
      QUALITY_CONTROL_ADMIN,
      QUALITY_CONTROL_MANAGER
    ]) || hasPermission(user.toJS(), [CAN_ADD_COMPANIES, CAN_UPDATE_COMPANIES])
)(props => (
  <Col md={6}>
    <label
      className="control-label"
      htmlFor={VENDOR_TRACE_PREAPPROVAL_REQUIRED}
    >
      <span>{properCase(VENDOR_TRACE_PREAPPROVAL_REQUIRED)}</span>
    </label>
    <FormGroup>
      <div
        className="btn-group btn-group-justified btn-group-vertical"
        data-toggle="buttons"
      >
        <Field
          name={VENDOR_TRACE_PREAPPROVAL_REQUIRED}
          component={Check}
          label={properCase(VENDOR_TRACE_PREAPPROVAL_REQUIRED)}
          disabled={props.disabled}
        />
      </div>
    </FormGroup>
  </Col>
));

const parseValue = value => (value === "" ? undefined : value);

const formatValue = value => (value === undefined ? "" : value);

const selector = formValueSelector("editApprovals");

const updatedAtField = (company, field) => {
  const updatedAt = get(company, `${field}_audit.updated_at`);
  if (!updatedAt) return null;
  return `<br />  Updated At ${shortDateYYandTime(updatedAt)}`;
};

export function Form(props) {
  const { company, otherApproved, setFieldValue } = props;

  const ECApprovedBy = _.get(company, "export_compliant_updated_by.full_name");
  const ECApprovalDate = shortDateYYandTime(
    company.export_compliant_updated_at
  );

  return (
    <AppForm {...props}>
      <AppForm.Body>
        <StatusSelect />
        <FormRow>
          <Col md={6}>
            <VendorApproval status={company.vendor_status} />
          </Col>
          <Col md={6}>
            <RepairStationApproval status={company.repair_station_status} />
          </Col>
        </FormRow>
        <FormRow>
          <BlanketExchangeAgreement />
          <DoNotQuote />
        </FormRow>
        <FormRow>
          <TracePreapproval />
          <Col md={6}>
            <label className="control-label" htmlFor={DO_NOT_EXCHANGE}>
              <span>{properCase(DO_NOT_EXCHANGE)}</span>
            </label>
            <FormGroup>
              <div
                className="btn-group btn-group-justified btn-group-vertical"
                data-toggle="buttons"
              >
                <Field
                  name={DO_NOT_EXCHANGE}
                  component={Check}
                  label={properCase(DO_NOT_EXCHANGE)}
                />
              </div>
            </FormGroup>
          </Col>
        </FormRow>
        <FormRow>
          <Col md={6}>
            <FormGroup>
              <label className="control-label" htmlFor="none">
                Other Approvals
              </label>
              <div
                className="btn-group btn-group-justified btn-group-vertical"
                data-toggle="buttons"
              >
                <ASASelect />
                <Field name={FAA_APPROVED} component={Check} label="FAA" />
                <Field name={EASA_APPROVED} component={Check} label="EASA" />
                <Field name={CAAC_APPROVED} component={Check} label="CAAC" />
                <Field name={TCCA_APPROVED} component={Check} label="TCCA" />
                <Field
                  name={UK_CAA_APPROVED}
                  component={Check}
                  label="UK CAA"
                />
                <Field name={OTHER_APPROVED} component={Check} label="OTHER" />
              </div>
            </FormGroup>
            {otherApproved && (
              <FormGroup>
                <Field
                  name="other_approval"
                  label="Other approval"
                  component={FormInput}
                  type="text"
                />
              </FormGroup>
            )}
          </Col>
          <Col md={6}>
            <FormGroup>
              <label className="control-label" htmlFor="none">
                Export Compliant
                {company.export_compliant && (
                  <NoteTooltip
                    note={`Approved by ${ECApprovedBy} on ${ECApprovalDate}`}
                    icon="fa-info-circle"
                    color="rgba(255, 255, 255, 0.25)"
                    placement="right"
                  />
                )}
              </label>
              <div
                className="btn-group btn-group-justified btn-group-vertical"
                data-toggle="buttons"
              >
                <ExportCompliant setFieldValue={setFieldValue} />
                <div>
                  <Field
                    name={END_USER_AGREEMENT_REQUESTED}
                    component={Check}
                    label={properCase(END_USER_AGREEMENT_REQUESTED)}
                    onChange={val => {
                      if (val) {
                        setFieldValue(EXPORT_COMPLIANT)(false);
                        setFieldValue(EXPORT_APPROVAL_REQUIRED)(false);
                      }
                    }}
                  />
                  {company.end_user_agreement_requested && (
                    <span>
                      <NoteTooltip
                        note={`Updated by ${_.get(
                          company,
                          "end_user_agreement_requested_by.full_name"
                        )} on ${shortDateYYandTime(
                          company.end_user_agreement_requested_at
                        )}`}
                        icon="fa-info-circle"
                        color="rgba(255, 255, 255, 0.25)"
                        placement="right"
                      />
                    </span>
                  )}
                </div>
                <div>
                  <Field
                    name={EXPORT_APPROVAL_REQUIRED}
                    component={Check}
                    label={properCase(EXPORT_APPROVAL_REQUIRED)}
                    onChange={val => {
                      if (val) {
                        setFieldValue(EXPORT_COMPLIANT)(false);
                        setFieldValue(END_USER_AGREEMENT_REQUESTED)(false);
                      }
                    }}
                  />
                  <span>
                    <NoteTooltip
                      note="All Sales Orders with company will be placed on Export Compliance Hold for review by QC."
                      icon="fa-info-circle"
                      color="rgba(255, 255, 255, 0.25)"
                      placement="right"
                    />
                  </span>
                </div>
              </div>
            </FormGroup>
            <FormGroup>
              <label className="control-label" htmlFor="none">
                Know Your Customer
              </label>
              <div>
                <label className="control-label" htmlFor="none">
                  {[
                    "Business Start Date",
                    <NoteTooltip
                      note={`February, 2022: Russian-related sanctions are made effective${updatedAtField(
                        company,
                        "business_start_date"
                      )}`}
                      icon="fa-info-circle"
                      color="rgba(255, 255, 255, 0.25)"
                      placement="right"
                    />
                  ]}
                </label>
                <FormRow>
                  <Col md={12}>
                    <MonthYearField
                      name="business_start_date"
                      className="form-control"
                      component="input"
                    />
                  </Col>
                </FormRow>
              </div>
            </FormGroup>
            <FormGroup>
              <FormRow>
                <Col md={12}>
                  <Field
                    name="customer_website_review_passed"
                    component={Select}
                    label={[
                      "Customer Website Review",
                      updatedAtField(
                        company,
                        "customer_website_review_passed"
                      ) ? (
                        <NoteTooltip
                          note={`Customer Website Review${updatedAtField(
                            company,
                            "customer_website_review_passed"
                          )}`}
                          icon="fa-info-circle"
                          color="rgba(255, 255, 255, 0.25)"
                          placement="right"
                        />
                      ) : null
                    ]}
                    options={toOptions(PASS_FAIL)}
                    parse={parseValue}
                    format={formatValue}
                  />
                </Col>
              </FormRow>
              <FormRow>
                <Col md={12}>
                  <Field
                    name="consolidated_screening_list_verification_passed"
                    component={Select}
                    label={[
                      "CSLV",
                      <NoteTooltip
                        note={`Consolidated Screening List Verification${updatedAtField(
                          company,
                          "cslv"
                        )}`}
                        icon="fa-info-circle"
                        color="rgba(255, 255, 255, 0.25)"
                        placement="right"
                      />
                    ]}
                    options={toOptions(PASS_FAIL)}
                    parse={parseValue}
                    format={formatValue}
                  />
                </Col>
              </FormRow>
              <FormRow>
                <Col md={12}>
                  <Field
                    name="research_social_media_networks_and_general_online_search_passed"
                    component={Select}
                    label={[
                      "RSMN",
                      <NoteTooltip
                        note={`Research Social Media Networks and General Online Search${updatedAtField(
                          company,
                          "rsmn"
                        )}`}
                        icon="fa-info-circle"
                        color="rgba(255, 255, 255, 0.25)"
                        placement="right"
                      />
                    ]}
                    options={toOptions(PASS_FAIL)}
                    parse={parseValue}
                    format={formatValue}
                  />
                </Col>
              </FormRow>
              <FormRow>
                <Col md={12}>
                  <Field
                    name="trader_partners_shipment_records_search_passed"
                    component={Select}
                    label={[
                      "TPSRS",
                      <NoteTooltip
                        note={`Trade Partners Shipment Records Search${updatedAtField(
                          company,
                          "tpsrs"
                        )}`}
                        icon="fa-info-circle"
                        color="rgba(255, 255, 255, 0.25)"
                        placement="right"
                      />
                    ]}
                    options={toOptions(PASS_FAIL)}
                    parse={parseValue}
                    format={formatValue}
                  />
                </Col>
              </FormRow>
            </FormGroup>
          </Col>
        </FormRow>

        <FormRow>
          <Col md={12}>
            <Field
              name={MANDATORY_ADDITIONAL_VERBIAGE}
              component={TextArea}
              label="Mandatory Additional Verbiage for Purchase Orders"
              rows={5}
            />
          </Col>
        </FormRow>
      </AppForm.Body>
    </AppForm>
  );
}

Form.propTypes = {
  company: T.shape({}),
  otherApproved: T.bool
};

const mapStateToProps = state => ({
  otherApproved: selector(state, OTHER_APPROVED)
});

export default connect(mapStateToProps, { onSubmit: actions.update })(
  form({
    form: "editApprovals", // Ensure this matches your form name
    validate
  })(withFieldAccessors(Form))
);



// WEBPACK FOOTER //
// ./app/modules/companies/components/EditApprovals/Form.jsx